import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  Calendar,
  CheckCircle,
  CreditCard,
  Crown,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';

import {
  changePlan,
  confirmCheckout,
  createSubscriptionCheckout,
  fetchPlansPublic,
  fetchStripeConfig,
  getStripeConfig,
  type FeatureDisplay,
  type PlanoPublic,
} from '../api/client';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/ui/PageHeader';
import { Skeleton } from '../components/ui/skeleton';
import { Spinner } from '../components/ui/spinner';
import { useNotification } from '../context/NotificationProvider';
import { notifyMutationError } from '../lib/errors';
import { formatDate, formatPrice } from '../lib/formatters';
import { useSubscription } from '../hooks/useSubscription';
import { queryPersistMeta } from '../lib/pwa/queryPersistence';
import { SecretarioAddonPlanCard, vaiPeloCheckout } from './assinatura/SecretarioAddonSection';
import { SecretarioDosEscritorios } from './assinatura/SecretarioDosEscritorios';
import {
  addonDisponivel,
  precoDoAddon,
  useOfertaSecretario,
  type Intervalo,
} from '../components/assinatura/SecretarioAddonCard';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Nome amigável por grupo de plano (cortesia expõe o `grupo`, não o nome).
const PLANO_GRUPO_LABELS: Record<string, string> = {
  free_trial: 'Trial Gratuito',
  basico: 'Básico',
  premium_ia: 'Premium IA',
};

function planoGrupoLabel(grupo: string): string {
  return PLANO_GRUPO_LABELS[grupo] ?? grupo;
}

// ---------------------------------------------------------------------------
// Toggle Mensal/Anual
// ---------------------------------------------------------------------------

function BillingToggle({
  interval,
  onChange,
}: {
  interval: 'MENSAL' | 'ANUAL';
  onChange: (v: 'MENSAL' | 'ANUAL') => void;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        variant={interval === 'MENSAL' ? 'default' : 'ghost'}
        size="sm"
        className="rounded-full"
        onClick={() => onChange('MENSAL')}
      >
        Mensal
      </Button>
      <Button
        variant={interval === 'ANUAL' ? 'default' : 'ghost'}
        size="sm"
        className="rounded-full"
        onClick={() => onChange('ANUAL')}
      >
        Anual
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeatureList
// ---------------------------------------------------------------------------

function FeatureList({ features }: { features: FeatureDisplay[] }) {
  const sorted = [...features].sort((a, b) => Number(b.included) - Number(a.included));
  return (
    <div className="mb-4 flex flex-1 flex-col gap-1.5">
      {sorted.map((f, idx) => (
        <div
          key={idx}
          className={`flex items-start gap-2 ${f.included ? '' : 'opacity-40'}`}
        >
          {f.included ? (
            <CheckCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />
          ) : (
            <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--color-text-tertiary)]" />
          )}
          <span
            className={`text-xs leading-snug ${
              f.included
                ? 'text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-tertiary)] line-through'
            }`}
          >
            {f.text}
          </span>
        </div>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// PlanPicker — grid de planos filtrado por intervalo
// ---------------------------------------------------------------------------

function PlanPicker({
  currentPlanGroup,
  hasCortesia,
  stripeReady,
  onSelect,
  selectingPlan,
  interval,
  trialFim,
  periodoFim,
  subscriptionStatus,
  extraCard,
}: {
  currentPlanGroup?: string;
  hasCortesia: boolean;
  stripeReady: boolean;
  onSelect: (code: string) => void;
  selectingPlan: string | null;
  interval: 'MENSAL' | 'ANUAL';
  trialFim?: string | null;
  periodoFim?: string | null;
  subscriptionStatus?: string;
  /** Card a mais na mesma grade, no fim (o Secretário de IA). */
  extraCard?: ReactNode;
}) {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const { data: plans, isLoading, isFetching, isError } = useQuery<PlanoPublic[]>({
    queryKey: [...queryKeys.plans.public],
    queryFn: fetchPlansPublic,
    meta: queryPersistMeta.never,
    staleTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
  });

  if (isLoading || (isFetching && !plans?.length)) {
    return (
      // Skeleton: exceção por layout shift
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-72 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 p-6">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-[var(--color-warning)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Não foi possível carregar os planos disponíveis.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Filtrar por intervalo selecionado.
  // Ocultar free_trial quando o usuário já está em plano pago (não pode downgrade para trial).
  const isOnPaidPlan = currentPlanGroup && currentPlanGroup !== 'free_trial';
  const filtered = (plans ?? []).filter(
    (p) => p.intervalo === interval && !(isOnPaidPlan && p.grupo === 'free_trial'),
  );

  if (!filtered.length) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Nenhum plano disponível no momento.
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleCardClick = (plan: PlanoPublic) => {
    const isCurrent = plan.grupo === currentPlanGroup;
    if (isCurrent || !!selectingPlan) return;
    setSelectedCode((prev) => (prev === plan.codigo ? null : plan.codigo));
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {filtered.map((plan) => {
        const isCurrent = plan.grupo === currentPlanGroup;
        const isSelected = selectedCode === plan.codigo;
        const isSelectingThis = selectingPlan === plan.codigo;
        const canSelect = !isCurrent && !selectingPlan;

        return (
          <Card
            key={plan.id}
            onClick={() => handleCardClick(plan)}
            className={`relative flex flex-col overflow-hidden transition-all ${
              canSelect ? 'cursor-pointer' : ''
            } ${
              isSelected
                ? 'ring-2 ring-[var(--color-primary)] border-[var(--color-primary)] shadow-lg shadow-[var(--color-primary)]/10'
                : isCurrent
                  ? 'border-muted-foreground/30 bg-muted/30'
                  : 'hover:border-[var(--color-primary)]/30'
            }`}
          >
            {/* Badge: plano atual, ou badge de marketing */}
            {isCurrent ? (
              <Badge variant="outline" className="absolute right-3 top-3">
                Plano atual
              </Badge>
            ) : plan.badge_text ? (
              <Badge className="absolute right-3 top-3 bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)]/90">
                <Sparkles className="mr-1 h-3 w-3" />
                {plan.badge_text}
              </Badge>
            ) : null}

            <CardContent className="flex flex-1 flex-col p-5">
              <h3 className="mb-1 text-base font-semibold text-[var(--color-text-primary)]">
                {plan.nome}
              </h3>

              <div className="mb-3">
                <span className="text-2xl font-bold text-[var(--color-text-primary)]">
                  {plan.preco_centavos === 0
                    ? 'Grátis'
                    : formatPrice(plan.preco_centavos)}
                </span>
                {plan.preco_centavos > 0 && (
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    /{plan.intervalo === 'ANUAL' ? 'ano' : 'mês'}
                  </span>
                )}
                {plan.trial_dias != null && plan.trial_dias > 0 && (
                  <span className="ml-2 text-xs text-[var(--color-text-tertiary)]">
                    ({plan.trial_dias} dias grátis)
                  </span>
                )}
              </div>

              {plan.descricao && (
                <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
                  {plan.descricao}
                </p>
              )}

              {plan.features_display && plan.features_display.length > 0 && (
                <FeatureList features={plan.features_display} />
              )}

              {/* Botão de ação — aparece para plano selecionado */}
              {isSelected && (
                <Button
                  className="mt-auto w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90"
                  disabled={!stripeReady || !!selectingPlan}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(plan.codigo);
                  }}
                >
                  {isSelectingThis && (
                    <Spinner className="mr-2 text-inherit" />
                  )}
                  {plan.cta_text || 'Assinar este plano'}
                </Button>
              )}

              {/* Botão fantasma para manter altura consistente quando não selecionado */}
              {!isCurrent && !isSelected && (
                <div className="mt-auto w-full text-center py-2 text-sm text-muted-foreground">
                  Clique para selecionar
                </div>
              )}

              {/* Info de período no card do plano atual.
                  Cortesia sobrepõe trial/cobrança: o acesso é concedido pelo
                  admin, sem data de expiração de trial nem cobrança. */}
              {isCurrent && hasCortesia && (
                <p className="mt-auto flex items-center gap-1 text-xs text-[var(--color-text-tertiary)]">
                  <Crown className="h-3 w-3" />
                  Cortesia ativa — sem cobrança
                </p>
              )}
              {isCurrent && !hasCortesia && trialFim && subscriptionStatus === 'TRIAL' && (
                <p className="mt-auto flex items-center gap-1 text-xs text-[var(--color-text-tertiary)]">
                  <Calendar className="h-3 w-3" />
                  Trial expira em {formatDate(trialFim)}
                </p>
              )}
              {isCurrent && !hasCortesia && periodoFim && subscriptionStatus !== 'TRIAL' && (
                <p className="mt-auto flex items-center gap-1 text-xs text-[var(--color-text-tertiary)]">
                  <Calendar className="h-3 w-3" />
                  Próxima cobrança: {formatDate(periodoFim)}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
      {extraCard}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe lazy-load (singleton)
// ---------------------------------------------------------------------------

let stripePromiseCache: ReturnType<typeof loadStripe> | null = null;

function getStripePromise(): ReturnType<typeof loadStripe> {
  if (!stripePromiseCache) {
    stripePromiseCache = getStripeConfig().then((config) =>
      loadStripe(config.publishable_key)
    );
  }
  return stripePromiseCache;
}

// ---------------------------------------------------------------------------
// CheckoutSection — Stripe Embedded Checkout
// ---------------------------------------------------------------------------

function CheckoutSection({
  planCode,
  addons,
  onCancel,
}: {
  planCode: string;
  addons: string[];
  onCancel: () => void;
}) {
  const addonsKey = addons.join(',');
  const fetchClientSecret = useCallback(
    () => createSubscriptionCheckout(planCode, addonsKey ? addonsKey.split(',') : []).then((r) => r.client_secret),
    [planCode, addonsKey],
  );

  return (
    <div className="space-y-3">
      <EmbeddedCheckoutProvider
        stripe={getStripePromise()}
        options={{ fetchClientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
      <Button variant="outline" className="w-full" onClick={onCancel}>
        Cancelar
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubscriptionPage
// ---------------------------------------------------------------------------

export default function SubscriptionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: subscription, isLoading, isError } = useSubscription();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  // Retorno do Checkout Session — confirmar no backend e atualizar estado
  const confirmingRef = useRef(false);
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || confirmingRef.current) return;
    confirmingRef.current = true;
    setSearchParams({}, { replace: true });

    confirmCheckout(sessionId)
      .then(() => {
        showSuccess('Assinatura ativada com sucesso!');
        queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      })
      .catch(() => {
        queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      });
  }, [searchParams, setSearchParams, queryClient, showSuccess]);

  const { data: stripeConfig, error: stripeError } = useQuery({
    queryKey: ['stripe', 'config'],
    queryFn: fetchStripeConfig,
    meta: queryPersistMeta.never,
    staleTime: 30 * 60 * 1000,
  });
  const [selectingPlan, setSelectingPlan] = useState<string | null>(null);
  const [interval, setInterval] = useState<'MENSAL' | 'ANUAL'>('MENSAL');

  const stripeReady = !!stripeConfig?.stripe_configured;
  const [payingPlan, setPayingPlan] = useState<string | null>(null);
  // O Secretário entra no mesmo checkout do plano (quem ainda não tem assinatura paga).
  const [incluirSecretario, setIncluirSecretario] = useState(false);
  // Os add-ons decididos NA ESCOLHA do plano: o switch só vale se o add-on está à
  // venda no intervalo do plano escolhido (senão o checkout recusaria com 503).
  const [payingAddons, setPayingAddons] = useState<string[]>([]);
  const { data: ofertaSecretario } = useOfertaSecretario();
  // Troca de intervalo com o add-on contratado: ele muda de cobrança junto.
  const [trocaComAddon, setTrocaComAddon] = useState<{ planCode: string; intervalo: Intervalo } | null>(null);

  const hasGatewaySubscription = !!subscription?.gateway_subscription_id;
  const addonContratado = subscription?.addons?.some((a) => a.codigo === 'secretario' && a.origem === 'STRIPE');

  const trocarPlano = async (planCode: string) => {
    setSelectingPlan(planCode);
    try {
      await changePlan(planCode);
      showSuccess('Plano atualizado com sucesso');
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setSelectingPlan(null);
    }
  };

  const handleSelectPlan = async (planCode: string) => {
    if (hasGatewaySubscription) {
      // Já tem assinatura Stripe — alterar plano diretamente
      if (addonContratado && subscription?.plano?.intervalo !== interval) {
        setTrocaComAddon({ planCode, intervalo: interval });
        return;
      }
      await trocarPlano(planCode);
    } else {
      // Sem assinatura Stripe — precisa de pagamento
      const comSecretario =
        incluirSecretario &&
        vaiPeloCheckout(subscription) &&
        !!ofertaSecretario &&
        stripeReady &&
        addonDisponivel(ofertaSecretario, interval);
      setPayingAddons(comSecretario ? ['secretario'] : []);
      setPayingPlan(planCode);
    }
  };


  if (isLoading) {
    return (
      // Skeleton: exceção por layout shift
      <div className="flex flex-col gap-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-8 w-32" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-72 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader icon={<CreditCard />} title="Assinatura" subtitle="Gerencie seu plano e pagamentos" />
        <Card>
          <CardContent className="flex items-center gap-3 p-6">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-[var(--color-warning)]" />
            <p className="text-sm text-[var(--color-text-secondary)]">
              Não foi possível carregar os dados da assinatura. Verifique sua
              conexão e tente novamente.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasCortesia = !!subscription?.cortesia;
  const pagoPeloEscritorio = subscription?.escritorio_que_paga ?? null;
  // Plano efetivo: a cortesia sobrepõe a subscription (o backend resolve os
  // limites na mesma ordem — ver resolve_plan_limits). Sem isso, o card de
  // trial era marcado como "Plano atual" mesmo com cortesia premium ativa.
  // Na vaga do escritório, nenhum plano é o "atual": o plano é o do escritório.
  const currentPlanGroup = hasCortesia
    ? subscription!.cortesia!.plano_grupo
    : subscription?.status === 'PENDING' || pagoPeloEscritorio
      ? undefined
      : subscription?.plano?.grupo;
  const trialExpired =
    subscription?.status === 'TRIAL' &&
    subscription?.trial_fim &&
    new Date(subscription.trial_fim) <= new Date();
  const isBlocked =
    !hasCortesia &&
    !pagoPeloEscritorio &&
    (!subscription ||
      ['EXPIRADA', 'CANCELADA', 'PENDING'].includes(
        subscription?.status ?? '',
      ) ||
      trialExpired);

  return (
    <div className="flex flex-col">
      <PageHeader icon={<CreditCard />} title="Assinatura" subtitle="Gerencie seu plano e pagamentos" />

      {/* Aviso de pagamento pendente (PAST_DUE = grace period) */}
      {subscription?.status === 'PAST_DUE' && (
        <Card className="mb-6 border-destructive bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-medium text-destructive">
                Pagamento pendente
              </p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Houve uma falha no pagamento da sua assinatura. Atualize seus
                dados de pagamento para evitar a suspensão do acesso.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Badge de cortesia */}
      {hasCortesia && (
        <Card className="mb-6 border-[var(--color-primary)]/20 bg-[var(--color-primary)]/[0.03]">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/10">
              <Crown className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--color-primary)]">
                Cortesia ativa: {planoGrupoLabel(subscription!.cortesia!.plano_grupo)}
              </p>
              {subscription!.cortesia!.motivo && (
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {subscription!.cortesia!.motivo}
                </p>
              )}
              <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                Acesso concedido pelo administrador — sem cobrança.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* A vaga paga pelo escritório */}
      {!hasCortesia && pagoPeloEscritorio && (
        <Card className="mb-6 border-[var(--color-primary)]/20 bg-[var(--color-primary)]/[0.03]">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/10">
              <Building2 className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--color-primary)]">
                Sua conta usa a assinatura do escritório {pagoPeloEscritorio.nome}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                O plano e os créditos de IA são os do escritório. Para ter um plano seu, escolha abaixo.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Planos disponíveis — esconde quando checkout está aberto */}
      {!payingPlan && (
      <div className="space-y-4">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {isBlocked ? 'Escolha seu plano' : 'Planos disponíveis'}
            </h2>
            {isBlocked && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                Assine um plano para utilizar o JurisApp com todas as funcionalidades.
              </p>
            )}
          </div>
          <BillingToggle interval={interval} onChange={setInterval} />
        </div>

        <PlanPicker
          currentPlanGroup={currentPlanGroup}
          hasCortesia={hasCortesia}
          stripeReady={stripeReady}
          onSelect={handleSelectPlan}
          selectingPlan={selectingPlan}
          interval={interval}
          trialFim={subscription?.trial_fim}
          periodoFim={subscription?.periodo_fim}
          subscriptionStatus={subscription?.status}
          extraCard={
            <SecretarioAddonPlanCard
              subscription={subscription}
              intervalo={interval}
              stripeReady={stripeReady}
              incluirNoCheckout={incluirSecretario}
              onIncluirNoCheckoutChange={setIncluirSecretario}
            />
          }
        />

        <SecretarioDosEscritorios subscription={subscription} stripeReady={stripeReady} />

        {/* Seção de pagamento Stripe */}
        {!stripeReady && (
          <p className="text-center text-xs text-[var(--color-text-tertiary)]">
            {stripeError
              ? 'Não foi possível carregar a configuração de pagamentos. Tente recarregar a página.'
              : 'Pagamentos serão habilitados em breve. Aguarde a configuração do sistema de pagamentos.'}
          </p>
        )}
      </div>
      )}

      {/* Seção de pagamento Stripe */}
      {payingPlan && (
        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 text-base font-semibold text-[var(--color-text-primary)]">
              Finalizar assinatura
            </h3>
            <CheckoutSection
              planCode={payingPlan}
              addons={payingAddons}
              onCancel={() => setPayingPlan(null)}
            />
          </CardContent>
        </Card>
      )}

      {trocaComAddon && ofertaSecretario && (
        <ConfirmDialog
          open
          title="Trocar de plano?"
          message={`O Secretário de IA acompanha o plano e passa a ${precoDoAddon(
            ofertaSecretario,
            trocaComAddon.intervalo,
          )}, com o valor proporcional na próxima fatura.`}
          confirmLabel="Trocar de plano"
          onConfirm={() => {
            const { planCode } = trocaComAddon;
            setTrocaComAddon(null);
            void trocarPlano(planCode);
          }}
          onCancel={() => setTrocaComAddon(null)}
        />
      )}
    </div>
  );
}
